import { Form, Row, Col } from 'antd';
import { Button } from '../button/button';
import styles from './sign-up-form.module.scss';
import TextField from '../form/text-field/text-field';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
  AT_LEAST_ONE_NUMBER_REGEX,
  AT_LEAST_ONE_SPECIAL_CHARACTER,
  AT_LEAST_ONE_UPPERCASE_LETTER,
} from '@/constants/password-regex.constants';

export type SignUpFieldType = {
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
  password: string;
  password_confirmation: string;
};

interface SignUpFormProps {
  onSubmit: (values: SignUpFieldType) => void;
  errors?: string[] | string;
  isLoading?: boolean;
}

export function SignUpForm({ onSubmit, errors, isLoading }: SignUpFormProps) {
  const { t } = useTranslation(['auth', 'common']);
  const [showError, setShowError] = useState(!!errors);
  const [form] = Form.useForm();

  useEffect(() => {
    if (errors) {
      setShowError(!!errors);
    }
  }, [errors]);

  return (
    <Form
      form={form}
      onFinish={onSubmit}
      onFieldsChange={() => setShowError(false)}
      autoComplete="off"
      className={styles.formContainer}
      requiredMark="optional"
      layout="vertical"
    >
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item<SignUpFieldType>
            label={t('auth:fields.firstName')}
            name="first_name"
            rules={[{ required: true, message: t('auth:validation.firstNameRequired') }]}
          >
            <TextField data-testid="first-name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item<SignUpFieldType>
            label={t('auth:fields.lastName')}
            name="last_name"
            rules={[{ required: true, message: t('auth:validation.lastNameRequired') }]}
          >
            <TextField data-testid="last-name" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item<SignUpFieldType>
        label={t('auth:fields.companyName')}
        name="company_name"
        rules={[{ required: true, message: t('auth:validation.companyNameRequired') }]}
      >
        <TextField data-testid="company-name" />
      </Form.Item>

      <Form.Item<SignUpFieldType>
        label={t('auth:fields.email')}
        name="email"
        rules={[
          { required: true, message: t('common:validation.emailRequired') },
          { type: 'email', message: t('common:validation.emailInvalid') },
        ]}
      >
        <TextField
          status={showError ? 'error' : undefined}
          data-testid="email"
          type="email"
          autoComplete="email"
          onPaste={(e) => {
            const pastedValue = e.clipboardData.getData('Text');
            form.setFieldsValue({ email: pastedValue.trim() });
            e.preventDefault();
          }}
        />
      </Form.Item>

      <Form.Item<SignUpFieldType>
        label={t('auth:fields.password')}
        name="password"
        rules={[
          { required: true, message: t('common:validation.passwordRequired') },
          { min: 8, message: t('auth:validation.passwordMinLength') },
          {
            pattern: AT_LEAST_ONE_NUMBER_REGEX,
            message: t('auth:validation.passwordNumber'),
          },
          {
            pattern: AT_LEAST_ONE_UPPERCASE_LETTER,
            message: t('auth:validation.passwordUppercase'),
          },
          {
            pattern: AT_LEAST_ONE_SPECIAL_CHARACTER,
            message: t('auth:validation.passwordSpecial'),
          },
        ]}
      >
        <TextField status={showError ? 'error' : undefined} data-testid="password" type="password" />
      </Form.Item>

      <Form.Item<SignUpFieldType>
        label={t('auth:fields.confirmPassword')}
        name="password_confirmation"
        dependencies={['password']}
        rules={[
          { required: true, message: t('auth:validation.confirmPasswordRequired') },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t('auth:validation.passwordsMismatch')));
            },
          }),
        ]}
      >
        <TextField
          status={showError ? 'error' : undefined}
          data-testid="confirm-password"
          type="password"
        />
      </Form.Item>

      <div className={styles.errorContainer}>
        {showError && (
          <span>
            {typeof errors === 'string' ? (
              <p className={styles.error}>{errors}</p>
            ) : (
              errors?.map((error, index) => (
                <p key={index} className={styles.error}>
                  {error}
                </p>
              ))
            )}
          </span>
        )}
      </div>

      <Form.Item className={styles.submitButton}>
        <Button
          size="large"
          type="primary"
          htmlType="submit"
          loading={isLoading}
          block
          disabled={showError}
        >
          {t('auth:signUp.submit')}
        </Button>
      </Form.Item>

      <p className={styles.signInLink}>
        <Trans
          ns="auth"
          i18nKey="signUp.haveAccount"
          components={{ link: <Link to="/sign-in" /> }}
        />
      </p>
    </Form>
  );
}
